import React from 'react';
import { Flex } from '@adobe/react-spectrum';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import ChevronDropdownIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import ChevronDownRiskIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
import GeneralInfo from '../GeneralInfo/GeneralInfo';
import PrimaryContacts from '../PrimaryContacts/PrimaryContacts';
import GeneralInfoCommentIcon from '../CommentPanel/GeneralInfoCommentIcon/GeneralCommentIcon';
import { EditOnlyWrapper } from '../context/RBACContext';
import { CalendarRange } from '../../Fields/Calendar';
import {
  SlideoutMilestoneAccordionHeader,
  SlideoutMilestoneAddFooterSlot
} from './SlideoutMilestonesSection';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import { DelayedTooltip } from '../DelayedTooltip';
import { MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS } from '../configs/momentTypeConstants';
import type { MomentData } from '../../../services/momentsApi';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';
import type { Contact as PCContact } from '../utils/primaryContactsManager';
import type { MilestoneSortPreferenceChoice } from '../hooks/useMilestoneSortPreference';
import styles from '../SlideOutPanel.module.scss';

export type SlideoutNonPhasedLayoutSectionProps = {
  card: SlideOutPanelCard | null | undefined;
  apiData: MomentData | null;
  generalInfo: Record<string, any>;
  contacts: any[];
  momentName: string;

  isNonPhasedAccordionExpanded: boolean;
  areAllSectionsCollapsed: () => boolean;
  toggleNonPhasedAccordion: () => void;
  toggleGeneralInfo: () => void;

  canEditGeneralInfo: boolean;
  canEditMilestones: boolean;

  expandedInfoSection: Set<string>;
  toggleInfoSection: (id: string) => void;

  statusButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleStatusToggle: (momentId: string) => void;
  formatStatusUpdatedDate: (date?: string) => string;

  nonPhasedDateChipOpen: boolean;
  setNonPhasedDateChipOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getAccordionDateDisplay: (
    momentType: string,
    dates: any
  ) => { fieldName: string; displayText: string };
  getAccordionCalendarProps: (dates: any, fieldName: string) => any;
  getDateChipTooltip: (fieldName: string) => string;
  handleAccordionDateChange: (
    phaseId: string | null,
    phaseName: string | undefined,
    fieldName: string,
    calendarDate: import('@internationalized/date').CalendarDate | null,
    isQuarterSelection: boolean | undefined,
    selectedQuarter: { quarter: number; year: number } | undefined,
    isMonthSelection: boolean | undefined,
    selectedMonth: { month: string; year: number } | undefined,
    specialValue: 'TBD' | 'N/A' | null | undefined,
    currentDates: any,
    oldDisplayText: string
  ) => void;

  tierButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleTierToggle: (momentId: string) => void;

  revenueImpactButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleRevenueImpactToggle: (momentId: string) => void;
  getRevenueImpactDisplayValue: (value: any, defaultNo: boolean) => string;

  riskButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  handleRiskLevelToggle: (momentId: string) => void;
  getRiskLevelIcon: (riskLevel: any) => string;

  renderStatusDropdownPortal: (momentId: string) => React.ReactNode;
  renderTierDropdownPortal: (momentId: string) => React.ReactNode;
  renderRevenueImpactDropdownPortal: (phaseId: string) => React.ReactNode;
  renderRiskLevelDropdownPortal: (momentId: string) => React.ReactNode;

  eventsData: any[];
  isLoadingEvents: boolean;
  eventsError: string | null;
  parentMomentsData: any[];
  isLoadingParentMoments: boolean;
  parentMomentsError: string | null;

  handleGeneralInfoCommentIconClick: (params: { phaseName: string }) => void;
  resolveMomentType: () => string;
  handleSeamlessDataRefreshWithoutFeatures: (patchResponse?: unknown) => void;
  handleSeamlessDataRefresh: (patchResponse?: unknown) => void;
  onLoadAlertsAndNotifications: () => void;
  onGeneralInfoChange: (updatedInfo: any) => void;
  refreshMomentNotifications: () => void;

  handleOpenPrimaryContactPopup: () => void;
  onRoleContactsReplace: (
    role: string,
    roleContacts: PCContact[]
  ) => Promise<void>;
  onContactUpdate: (updatedContact: PCContact, index: number) => Promise<void>;

  isMilestonesExpanded: boolean;
  getMilestoneArray: () => any[];
  handleSortToggle: (key: string) => void;
  getSortDescriptiveText: (key: string) => string;
  sortDropdownOpen: Record<string, boolean>;
  getEffectiveMilestoneSort: (key: string) => string;
  handleSortSelect: (key: string, sort: MilestoneSortPreferenceChoice) => void;
  milestoneUpdateTrigger: number;
  milestoneMenuOpen: Record<string, boolean>;
  handleMilestoneMenuToggle: (menuKey: string) => void;
  handleDeleteMilestones: (
    phaseId?: string,
    phaseName?: string
  ) => Promise<void>;

  hasPhases: boolean;
  hasMomentLevelMilestones: boolean;
  areMilestonesInherited: () => boolean;
  shouldShowAddMilestoneButton: () => boolean;
  getReleasePhaseMilestones: () => any[];
  userMilestoneAdded: boolean;
  renderMilestoneMatrix: () => React.ReactNode;
  renderInheritedMilestones: () => React.ReactNode;
  renderReleasePhaseMilestones: () => React.ReactNode;
  getMilestones: () => any;
  seamlessRefreshAndNotifications: () => void;
};

/**
 * Non-phased moment accordion layout (Part 3A-1).
 */
export function SlideoutNonPhasedLayoutSection({
  card,
  apiData,
  generalInfo,
  contacts,
  momentName,
  isNonPhasedAccordionExpanded,
  areAllSectionsCollapsed,
  toggleNonPhasedAccordion,
  toggleGeneralInfo,
  canEditGeneralInfo,
  canEditMilestones,
  expandedInfoSection,
  toggleInfoSection,
  statusButtonRefs,
  handleStatusToggle,
  formatStatusUpdatedDate,
  nonPhasedDateChipOpen,
  setNonPhasedDateChipOpen,
  getAccordionDateDisplay,
  getAccordionCalendarProps,
  getDateChipTooltip,
  handleAccordionDateChange,
  tierButtonRefs,
  handleTierToggle,
  revenueImpactButtonRefs,
  handleRevenueImpactToggle,
  getRevenueImpactDisplayValue,
  riskButtonRefs,
  handleRiskLevelToggle,
  getRiskLevelIcon,
  renderStatusDropdownPortal,
  renderTierDropdownPortal,
  renderRevenueImpactDropdownPortal,
  renderRiskLevelDropdownPortal,
  eventsData,
  isLoadingEvents,
  eventsError,
  parentMomentsData,
  isLoadingParentMoments,
  parentMomentsError,
  handleGeneralInfoCommentIconClick,
  resolveMomentType,
  handleSeamlessDataRefreshWithoutFeatures,
  handleSeamlessDataRefresh,
  onLoadAlertsAndNotifications,
  onGeneralInfoChange,
  refreshMomentNotifications,
  handleOpenPrimaryContactPopup,
  onRoleContactsReplace,
  onContactUpdate,
  isMilestonesExpanded,
  getMilestoneArray,
  handleSortToggle,
  getSortDescriptiveText,
  sortDropdownOpen,
  getEffectiveMilestoneSort,
  handleSortSelect,
  milestoneUpdateTrigger,
  milestoneMenuOpen,
  handleMilestoneMenuToggle,
  handleDeleteMilestones,
  hasPhases,
  hasMomentLevelMilestones,
  areMilestonesInherited,
  shouldShowAddMilestoneButton,
  getReleasePhaseMilestones,
  userMilestoneAdded,
  renderMilestoneMatrix,
  renderInheritedMilestones,
  renderReleasePhaseMilestones,
  getMilestones,
  seamlessRefreshAndNotifications
}: SlideoutNonPhasedLayoutSectionProps) {
  const rawMomentType = card?.momentType || generalInfo?.momentType;

  const momentTypeMapping: Record<string, string> = {
    'Offer, Price Change or Program': 'Offers and Promos',
    'Offer, Price Change, or Program': 'Offers and Promos',
    [MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS]:
      MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS,
    'Release w/ Marketing': 'Release w/ Marketing',
    'Release w/o Marketing': 'Release w/o Marketing',
    'Marketing Moment': 'Marketing Moment',
    'Policy Change': 'Policy Change',
    'Key Date': 'Key Date',
    Event: 'Event'
  };

  const momentType = rawMomentType
    ? momentTypeMapping[rawMomentType] || rawMomentType
    : 'Moment';

  const momentId = card?.issueId || 'moment-section';
  const accordionExpanded = isNonPhasedAccordionExpanded;

  return (
    <div
      data-testid="slideout-moment-accordion"
      className={`${styles.phaseAccordionContainer} ${
        areAllSectionsCollapsed() ? styles.allCollapsed : ''
      }`}
    >
      <div
        key={momentId}
        data-testid={`slideout-moment-card-${momentId}`}
        className={styles.phaseCard}
      >
        <div
          data-testid={`slideout-moment-header-${momentId}`}
          className={styles.phaseHeader}
        >
          <div
            data-testid={`slideout-moment-toggle-${momentId}`}
            className={styles.phaseToggle}
            onClick={() => toggleNonPhasedAccordion()}
            style={{ cursor: 'pointer' }}
          >
            <ChevronIcon isExpanded={accordionExpanded} />
          </div>
          <div
            data-testid={`slideout-moment-title-${momentId}`}
            className={styles.phaseTitle}
          >
            <span
              data-testid={`slideout-moment-name-${momentId}`}
              className={styles.phaseName}
              onClick={() => toggleGeneralInfo()}
              style={{ cursor: 'pointer' }}
            >
              {momentType || 'Moment'}
            </span>
            <span
              data-testid={`slideout-moment-status-${momentId}`}
              className={styles.phaseStatus}
            >
              {(() => {
                const statusLabel =
                  generalInfo?.reportingStatus?.split(' - ')[1];
                if (!statusLabel) return null;
                return (
                  <span
                    data-testid={`slideout-moment-status-badge-${momentId}`}
                    className={`${styles.statusBadge} ${
                      styles[statusLabel.toLowerCase()]
                    }`}
                  >
                    {statusLabel}
                  </span>
                );
              })()}
              <div
                data-testid={`slideout-moment-status-dropdown-wrapper-${momentId}`}
                className={styles.dropdownWrapper}
                style={{ position: 'relative' }}
                data-moment-id={momentId}
              >
                {/* Completed is only shown when BOTH the primary status and the secondary date
                    status (when this moment type has one) are done — either one still Active
                    means the moment as a whole reads Active. No secondaryDateStatus at all
                    (null/undefined, e.g. Marketing Moment) never blocks on it, matching the
                    backend's own null-tolerant semantics (isReleasePhaseSecondaryDone). The
                    "Completed on <date>" date itself is still always the primary's own
                    statusUpdatedAt — unchanged. */}
                {(() => {
                  const status = apiData?.status || card?.status;
                  const secondaryDateStatus =
                    apiData?.secondaryDateStatus || card?.secondaryDateStatus;
                  const normalizedSecondary = (
                    secondaryDateStatus || ''
                  ).toUpperCase();
                  const isSecondaryDone =
                    secondaryDateStatus == null ||
                    normalizedSecondary === 'COMPLETE' ||
                    normalizedSecondary === 'COMPLETED';
                  const isFullyCompleted =
                    status === 'COMPLETED' && isSecondaryDone;

                  return (
                    <button
                      data-testid={`slideout-moment-status-dropdown-button-${momentId}`}
                      ref={el => {
                        if (el) {
                          statusButtonRefs.current[momentId] = el;
                        }
                      }}
                      className={
                        isFullyCompleted
                          ? `${styles.activeBadgeDropdown} ${styles.completedBadgeDropdown}`
                          : styles.activeBadgeDropdown
                      }
                      aria-label="Status options"
                      style={{
                        cursor: canEditGeneralInfo ? 'pointer' : 'default'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (
                          canEditGeneralInfo &&
                          statusButtonRefs.current[momentId]
                        ) {
                          handleStatusToggle(momentId);
                        }
                      }}
                    >
                      <span>
                        {isFullyCompleted
                          ? `Completed on ${formatStatusUpdatedDate(
                              apiData?.statusUpdatedAt || card?.statusUpdatedAt
                            )}`
                          : status === 'REMOVE'
                          ? 'Remove'
                          : 'Active'}
                      </span>
                      <span className={styles.dropdownArrow}>
                        <img src={ChevronDropdownIcon} alt="dropdown" />
                      </span>
                    </button>
                  );
                })()}
                {renderStatusDropdownPortal(momentId)}
              </div>
            </span>

            {(() => {
              const npDates = generalInfo?.dates;
              const { fieldName: npDateField, displayText: npDateText } =
                getAccordionDateDisplay(momentType, npDates);
              const npCalProps = getAccordionCalendarProps(
                npDates,
                npDateField
              );
              return (
                <div
                  className={styles.dropdownWrapper}
                  style={{ position: 'relative' }}
                  data-testid={`slideout-moment-date-chip-wrapper-${momentId}`}
                >
                  <DelayedTooltip
                    message={getDateChipTooltip(npDateField)}
                    cursor="pointer"
                    ariaLabel="Edit date"
                  >
                    <button
                      data-testid={`slideout-moment-date-chip-button-${momentId}`}
                      className={styles.tierBadgeDropdown}
                      aria-label="Edit date"
                      style={{
                        cursor: canEditGeneralInfo ? 'pointer' : 'default'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (canEditGeneralInfo) {
                          setNonPhasedDateChipOpen(prev => !prev);
                        }
                      }}
                    >
                      <span className={styles.tierText}>{npDateText}</span>
                      <span className={styles.dropdownArrow}>
                        <img src={ChevronDownRiskIcon} alt="dropdown" />
                      </span>
                    </button>
                  </DelayedTooltip>
                  {nonPhasedDateChipOpen && (
                    <div className={styles.calendarDropdown}>
                      <CalendarRange
                        mode="single"
                        skipTriggerButton={true}
                        hideYearList={true}
                        showTBDNA={true}
                        disablePastDate={false}
                        defaultSpecialValue={npCalProps.defaultSpecialValue}
                        defaultDate={npCalProps.defaultDate}
                        defaultMonth={npCalProps.defaultMonth}
                        defaultQuarter={npCalProps.defaultQuarter}
                        disableNA={npCalProps.disableNA}
                        disableTBD={npCalProps.disableTBD}
                        onCalendarDateChange={(
                          date,
                          isQuarter,
                          selQuarter,
                          isMonth,
                          selMonth,
                          special
                        ) => {
                          handleAccordionDateChange(
                            null,
                            undefined,
                            npDateField,
                            date,
                            isQuarter,
                            selQuarter,
                            isMonth,
                            selMonth,
                            special,
                            npDates,
                            npDateText
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            <div
              className={styles.dropdownWrapper}
              style={{ position: 'relative' }}
              data-moment-id={momentId}
            >
              <button
                ref={el => {
                  if (el) {
                    tierButtonRefs.current[momentId] = el;
                  }
                }}
                className={styles.tierBadgeDropdown}
                aria-label="Tier options"
                style={{
                  cursor: canEditGeneralInfo ? 'pointer' : 'default'
                }}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (canEditGeneralInfo && tierButtonRefs.current[momentId]) {
                    handleTierToggle(momentId);
                  }
                }}
              >
                <span className={styles.tierText}>
                  {apiData?.generalInfo?.tier || 'L1'}
                </span>
                <span className={styles.dropdownArrow}>
                  <img src={ChevronDownRiskIcon} alt="dropdown" />
                </span>
              </button>
              {renderTierDropdownPortal(momentId)}
            </div>

            <div
              className={styles.dropdownWrapper}
              style={{ position: 'relative' }}
              data-moment-id={momentId}
            >
              <ToolTipWrapper message={`Impacts Revenue Targets`}>
                <button
                  ref={el => {
                    if (el) {
                      revenueImpactButtonRefs.current[momentId] = el;
                    }
                  }}
                  className={styles.tierBadgeDropdown}
                  aria-label="$ Revenue Impact options"
                  style={{
                    cursor: canEditGeneralInfo ? 'pointer' : 'default'
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (
                      canEditGeneralInfo &&
                      revenueImpactButtonRefs.current[momentId]
                    ) {
                      handleRevenueImpactToggle(momentId);
                    }
                  }}
                >
                  <span className={styles.tierText}>
                    $:{' '}
                    {getRevenueImpactDisplayValue(
                      apiData?.generalInfo?.revenueImpact,
                      false
                    )}
                  </span>
                  <span className={styles.dropdownArrow}>
                    <img src={ChevronDownRiskIcon} alt="dropdown" />
                  </span>
                </button>
              </ToolTipWrapper>
              {renderRevenueImpactDropdownPortal(momentId)}
            </div>

            <div
              className={styles.dropdownWrapper}
              style={{ position: 'relative' }}
              data-moment-id={momentId}
            >
              {(() => {
                const status = apiData?.status || card?.status;
                return status === 'COMPLETED' ? (
                  <div className={styles.completedRiskIndicator}>
                    <CheckMarkCircleIcon width="18" height="18" />
                  </div>
                ) : (
                  <>
                    <button
                      ref={el => {
                        if (el) {
                          riskButtonRefs.current[momentId] = el;
                        }
                      }}
                      className={styles.riskLevelBadgeDropdown}
                      aria-label="Risk level options"
                      style={{
                        cursor: canEditGeneralInfo ? 'pointer' : 'default'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (
                          canEditGeneralInfo &&
                          riskButtonRefs.current[momentId]
                        ) {
                          handleRiskLevelToggle(momentId);
                        }
                      }}
                    >
                      <span className={styles.riskLevelIcon}>
                        <img
                          src={getRiskLevelIcon(
                            apiData?.generalInfo?.riskLevel
                          )}
                          alt="risk level"
                        />
                      </span>
                      <span className={styles.dropdownArrow}>
                        <img src={ChevronDownRiskIcon} alt="dropdown" />
                      </span>
                    </button>
                    {renderRiskLevelDropdownPortal(momentId)}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        {accordionExpanded && (
          <div className={styles.phaseContent}>
            <div className={styles.phaseInfoContactsSection}>
              <div className={styles.phaseGeneralInfo}>
                <div
                  className={styles.infoSectionHeader}
                  onClick={() => toggleInfoSection(momentId)}
                >
                  <div className={styles.infoHeaderContent}>
                    <div className={styles.phaseToggle}>
                      <ChevronIcon
                        isExpanded={expandedInfoSection.has(momentId)}
                      />
                    </div>
                    <Flex direction="row" alignItems="center">
                      <span className={styles.sectionTitleText}>
                        General Info
                      </span>
                      <GeneralInfoCommentIcon
                        phaseName="General Info"
                        onClick={() =>
                          handleGeneralInfoCommentIconClick({
                            phaseName: 'General Info'
                          })
                        }
                      />
                    </Flex>
                  </div>
                </div>
                {expandedInfoSection.has(momentId) && (
                  <div className={styles.phaseSectionContent}>
                    <GeneralInfo
                      data={
                        {
                          ...generalInfo,
                          reportingStatus: apiData?.status || card?.status
                        } as any
                      }
                      eventsData={eventsData}
                      isLoadingEvents={isLoadingEvents}
                      eventsError={eventsError}
                      parentMomentsData={parentMomentsData}
                      isLoadingParentMoments={isLoadingParentMoments}
                      parentMomentsError={parentMomentsError}
                      isPhased={false}
                      issueId={card?.issueId}
                      rootMomentName={(() => {
                        const name = apiData?.name || card?.name || card?.title;
                        console.log('🔍 Passing to GeneralInfo (non-phased):', {
                          rootMomentName: name,
                          rootMomentType:
                            apiData?.momentType || resolveMomentType(),
                          apiDataName: apiData?.name,
                          cardName: card?.name,
                          apiDataMomentType: apiData?.momentType,
                          issueId: card?.issueId
                        });
                        return name;
                      })()}
                      rootMomentType={
                        apiData?.momentType || resolveMomentType()
                      }
                      onMilestoneRefresh={
                        handleSeamlessDataRefreshWithoutFeatures
                      }
                      onSeamlessRefresh={handleSeamlessDataRefresh}
                      onRefreshNotifications={onLoadAlertsAndNotifications}
                      onGeneralInfoChange={onGeneralInfoChange}
                      onNotificationRefresh={refreshMomentNotifications}
                    />
                  </div>
                )}
              </div>
              <div className={styles.phasePrimaryContacts}>
                <div className={styles.infoSectionHeader}>
                  <div className={styles.infoHeaderContent}>
                    <span className={styles.sectionTitleText}>
                      Primary Contacts
                    </span>
                  </div>
                  <EditOnlyWrapper>
                    <button
                      className={styles.addButtonPrimary}
                      onClick={() => {
                        handleOpenPrimaryContactPopup();
                      }}
                      aria-label="Add primary contact"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8 3V13M3 8H13"
                          stroke="#909090"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </EditOnlyWrapper>
                </div>
                {expandedInfoSection.has(momentId) && (
                  <div className={styles.phaseSectionContent}>
                    <PrimaryContacts
                      contacts={contacts}
                      showHeader={false}
                      onExternalAddClick={() => {}}
                      onRoleContactsReplace={async (role, roleContacts) => {
                        await onRoleContactsReplace(
                          role,
                          roleContacts as PCContact[]
                        );
                      }}
                      onContactUpdate={async (updatedContact, index) => {
                        await onContactUpdate(
                          updatedContact as PCContact,
                          index
                        );
                      }}
                      onContactAdd={() => {}}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.phaseMilestones}>
              <div
                className={`${styles.milestonesSection} ${
                  getMilestoneArray()?.length === 0
                    ? styles.milestonesSectionEmpty
                    : ''
                }`}
              >
                <SlideoutMilestoneAccordionHeader
                  showControls={isMilestonesExpanded}
                  titleSlot={
                    <Flex direction="row" alignItems="center">
                      <span className={styles.sectionTitleText}>
                        Milestones
                      </span>
                      <GeneralInfoCommentIcon
                        phaseName="Milestones"
                        onClick={() =>
                          handleGeneralInfoCommentIconClick({
                            phaseName: 'Milestones'
                          })
                        }
                      />
                    </Flex>
                  }
                  sortKey={`nonphased-${card?.issueId}`}
                  sortDropdownOpen={sortDropdownOpen}
                  getSortDescriptiveText={getSortDescriptiveText}
                  getEffectiveMilestoneSort={getEffectiveMilestoneSort}
                  handleSortToggle={handleSortToggle}
                  handleSortSelect={handleSortSelect}
                  milestones={getMilestoneArray()}
                  menuKey={`nonphased-menu-${card?.issueId}`}
                  menuReactKey={`milestone-menu-${milestoneUpdateTrigger}`}
                  momentName={card?.name}
                  milestoneMenuOpen={milestoneMenuOpen}
                  handleMilestoneMenuToggle={handleMilestoneMenuToggle}
                  handleDeleteMilestones={handleDeleteMilestones}
                />
                <div className={styles.milestonesContent}>
                  {(!hasPhases || hasMomentLevelMilestones) &&
                    renderMilestoneMatrix()}

                  {hasPhases && !hasMomentLevelMilestones && (
                    <>
                      {areMilestonesInherited() && renderInheritedMilestones()}

                      {(!areMilestonesInherited() &&
                        shouldShowAddMilestoneButton() === false) ||
                      areMilestonesInherited() ||
                      getReleasePhaseMilestones().length > 0 ? (
                        <div>{renderReleasePhaseMilestones()}</div>
                      ) : null}

                      {!areMilestonesInherited() && userMilestoneAdded && (
                        <div>{renderMilestoneMatrix()}</div>
                      )}
                    </>
                  )}
                </div>

                <SlideoutMilestoneAddFooterSlot
                  visible={
                    shouldShowAddMilestoneButton() &&
                    !!(getMilestones()?.issueId || card?.issueId)
                  }
                  onDataRefresh={seamlessRefreshAndNotifications}
                  issueId={getMilestones()?.issueId || card?.issueId!}
                  milestoneId={getMilestones()?.milestoneId}
                  momentName={momentName}
                  momentType={momentType}
                  disabled={!canEditMilestones}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
